// === SAA PRODUCT CONTENT MODEL ===
// Typed accessor over productContent.json — the single source of truth for
// per-product customer-facing content: display-name/description/dekking i18n
// keys, voorwaarden + verzekeringskaart documents and product-bound legal copy.
//
// Translatable text stays in src/i18n/*.json (NL/EN); this model references it
// by key so every surface (product selection, offer page, confirmation,
// breakdown card) resolves product text through ONE place. Documents point at
// public/attachments.
//
// Missing authoritative content is an explicit `TODO(content): ...` marker,
// never lorem or invented text. scripts/check-product-content.mjs
// (`npm run check:content`) reports every marker, verifies the referenced
// PDFs exist and the i18n keys resolve; surfaces render a visible
// "ONTBREEKT" row in dev (uiFlags.SHOW_MISSING_CONTENT_MARKERS) instead of
// silently dropping TODO documents.
//
// Like registry.ts this module stays dependency-light (type-only imports) so
// pure utils and the guard script can rely on it without circular-import risk.

import type { SaaProductId } from '../utils/addressSharing';
import contentData from './productContent.json';

export const CONTENT_TODO_PREFIX = 'TODO(content):';

/** A field whose authoritative value is still missing holds a marker string. */
export const isContentTodo = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith(CONTENT_TODO_PREFIX);

export interface ProductDocument {
  /** Public URL, served from public/ (today always under /attachments). */
  url: string;
  /** Download filename. */
  fileName: string;
  /** Official document/model code + version — TODO(content) until SAA supplies it. */
  documentCode: string;
}

/** Verzekeringskaart differs per hoofddekking (personenauto WA / WA+ / WA+volledig). */
export interface VerzekeringskaartVariants {
  defaultCoverage: string;
  byCoverage: Record<string, ProductDocument>;
}

export type CoverageInfoItemKeys = string | { labelKey: string; textKey: string };

export interface ProductContentEntry {
  /** i18n key for the product-card display name (products.<id>.name). */
  displayNameKey: string;
  /** i18n key for the offer/summary/document-list name (screens.offerSummary.productNames.<id>). */
  offerNameKey: string;
  /** i18n key for the one-line tagline on the product-selection card. */
  descriptionKey: string;
  /** i18n key for the "Wat dekt ...?" info-popup title. */
  infoTitleKey: string;
  /** i18n keys for the short dekking bullets (must match the official verzekeringskaart), or a TODO marker. */
  dekkingSummaryKeys: string[] | string;
  /** "Wat dekt ...?" popup bullets on the selection card: the SAME i18n keys the product screen renders, so the two pages cannot drift. */
  coverageInfoKeys: CoverageInfoItemKeys[];
  /** i18n key for the eigen risico line (selection card + "Wat dekt ...?" popup), from the verzekeringskaart. */
  deductibleKey: string;
  /** Polisvoorwaarden document, or a TODO marker while the official PDF is missing. */
  voorwaarden: ProductDocument | string;
  /** Verzekeringskaart document (possibly per-coverage), or a TODO marker. */
  verzekeringskaart: ProductDocument | VerzekeringskaartVariants | string;
  /** Extra product-bound documents beyond voorwaarden/verzekeringskaart (e.g. algemene voorwaarden). */
  extraDocuments?: ProductDocument[];
  /** i18n keys for product-bound legal/consent copy (e.g. personenauto alarm notice). */
  legalNoticeKeys?: string[];
}

const data = contentData as unknown as {
  generalTerms: ProductDocument;
  products: Record<SaaProductId, ProductContentEntry>;
};

/** Algemene Voorwaarden (not product-bound, linked from privacy/consent copy). */
export const GENERAL_TERMS: ProductDocument = data.generalTerms;

export const PRODUCT_CONTENT: Record<SaaProductId, ProductContentEntry> = data.products;

export const getProductContent = (id: SaaProductId): ProductContentEntry => PRODUCT_CONTENT[id];

/** Dekking bullets for a product; empty while the content is still a TODO marker. */
export const getDekkingSummaryKeys = (id: SaaProductId): string[] => {
  const keys = PRODUCT_CONTENT[id].dekkingSummaryKeys;
  return isContentTodo(keys) ? [] : (keys as string[]);
};

export const getCoverageInfoKeys = (id: SaaProductId): CoverageInfoItemKeys[] =>
  PRODUCT_CONTENT[id].coverageInfoKeys ?? [];

export interface ResolvedProductDocument {
  kind: 'voorwaarden' | 'verzekeringskaart' | 'algemeneVoorwaarden';
  /** null while the document is still a TODO marker. */
  doc: ProductDocument | null;
  /** The TODO(content) note when doc is null. */
  todo: string | null;
}

const resolveDocument = (
  kind: ResolvedProductDocument['kind'],
  value: ProductDocument | VerzekeringskaartVariants | string,
  coverage?: string,
): ResolvedProductDocument => {
  if (isContentTodo(value)) return { kind, doc: null, todo: value };
  if ('byCoverage' in (value as VerzekeringskaartVariants)) {
    const variants = value as VerzekeringskaartVariants;
    const doc = variants.byCoverage[coverage ?? variants.defaultCoverage]
      ?? variants.byCoverage[variants.defaultCoverage];
    return { kind, doc, todo: null };
  }
  return { kind, doc: value as ProductDocument, todo: null };
};

/**
 * The voorwaarden + verzekeringskaart for a product, with TODO markers kept
 * explicit so surfaces can render an ONTBREEKT row instead of dropping them.
 * Pass the selected hoofddekking for products with per-coverage cards.
 */
export const getProductDocuments = (
  id: SaaProductId,
  opts?: { coverage?: string },
): ResolvedProductDocument[] => {
  const entry = PRODUCT_CONTENT[id];
  return [
    resolveDocument('voorwaarden', entry.voorwaarden),
    resolveDocument('verzekeringskaart', entry.verzekeringskaart, opts?.coverage),
    ...(entry.extraDocuments ?? []).map((doc) => resolveDocument('algemeneVoorwaarden', doc)),
  ];
};

export interface ContentTodo {
  productKey: string;
  field: string;
  note: string;
}

/** Every TODO(content) marker in the model, for the guard script and dev warning. */
export const listContentTodos = (): ContentTodo[] => {
  const todos: ContentTodo[] = [];
  const visit = (productKey: string, field: string, value: unknown): void => {
    if (isContentTodo(value)) {
      todos.push({ productKey, field, note: value });
    } else if (Array.isArray(value)) {
      value.forEach((v, i) => visit(productKey, `${field}[${i}]`, v));
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => visit(productKey, field ? `${field}.${k}` : k, v));
    }
  };
  visit('generalTerms', '', GENERAL_TERMS);
  Object.entries(PRODUCT_CONTENT).forEach(([id, entry]) => visit(id, '', entry));
  return todos;
};

let warnedOnce = false;

/** Dev-only console warning listing all missing content fields (once per session). */
export const warnMissingProductContent = (): void => {
  if (warnedOnce || !import.meta.env.DEV) return;
  warnedOnce = true;
  const todos = listContentTodos();
  if (todos.length === 0) return;
  console.warn(
    `[product-content] ${todos.length} ontbrekende contentvelden (zie npm run check:content):\n`
    + todos.map((t) => `  - ${t.productKey}${t.field ? `.${t.field}` : ''}`).join('\n'),
  );
};
